import { useEffect, type ReactNode } from 'react';

interface Props {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: ReactNode;
}

/**
 * Mobile bottom action sheet — slides up from the bottom, respects the
 * home-indicator safe area. Adapted from the ProfileModal overlay pattern.
 */
export function BottomActionSheet({ open, onClose, title, children }: Props) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-t-2xl bg-white p-4 pb-safe-bottom shadow-xl sm:rounded-2xl sm:pb-4 animate-in slide-in-from-bottom">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300 sm:hidden" />
                {title && <h3 className="mb-2 text-center text-sm font-semibold text-gray-500">{title}</h3>}
                {children}
            </div>
        </div>
    );
}
